import { Dispatch } from "@reduxjs/toolkit";
import { MVisionAppClient } from "../store/mvision-client-list";
import { AccessRights } from "../store/access-rights";
import { DeploymentConfigInfo } from "../store/deployment-config-info";

/** Query parameter used by backend API to specify caller's session ID. */
export const QUERY_PARAM_SESSION_ID = 'sessionId';

export type JsonValue = string | string[] | number | undefined;

export type BundledVersionInfoJson = {
    labeling: any,
    backendVersion: any,
};

/**
 * Generic backend client API wrapper interface. All backend
 * client classes should implement this interface.
 * 
 * Note that the application does not support changing the used backend client
 * on the fly. Each different backend client should have their own build (and
 * deployment, if relevant) of the entire application.
 */
export default interface BackendApiInterface {
    /** One-time initialization of the backend client with the application. */
    init(dispatch: Dispatch, deploymentInfo: DeploymentConfigInfo): void,
    
    fetchModelConfigurationFileAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    saveModelConfigurationAsync(appClient: MVisionAppClient | undefined, json: string): Promise<boolean>,
    deleteModelConfigurationAsync(appClient: MVisionAppClient | undefined): Promise<boolean>,

    /** Returns default configuration for a single contouring model */
    fetchDefaultConfigurationForModelAsync(modelName: string): Promise<any>,

    fetchLicenseStatusAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    fetchAllDaemonConfigsAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    fetchDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<any>,
    saveDaemonConfigAsync(appClient: MVisionAppClient | undefined, json: string): Promise<boolean>,
    resetDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean>,
    deleteDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean>,
    saveLicenseAsync(appClient: MVisionAppClient | undefined, newLicense: string): Promise<boolean>,
    fetchLabelingAsync(): Promise<BundledVersionInfoJson>,
    fetchActiveModelsAsync(appClient: MVisionAppClient | undefined): Promise<any>,
    fetchClientListAsync(): Promise<any>,
    fetchAccessRightsAsync(): Promise<any>,
    
    getUserSettingsAsync(): Promise<any>;
    saveUserSettingAsync(setting: string, jsonValue: string): Promise<boolean>;
    saveUserSettingsAsync(settingsJson: { [x: string]: JsonValue; }): Promise<boolean>;

    /** Returns a component that will wrap the entire application */
    getAppWrapperComponent(): (props: React.PropsWithChildren<{}>) => JSX.Element,
}

